<script setup>
    import AgendaDetailCard from '~/components/blocks/two-six/agenda/detail-card.vue';
    import { useScheduleReasons } from '@/composables/useScheduleReasons';

    const props = defineProps({
        session: {
            type: Object,
            required: true,
        },
        variant: {
            type: String,
            default: 'page', // 'page' | 'drawer'
            validator: (v) => ['page', 'drawer'].includes(v),
        },
    });

    // Always scope to the parent session's year so related sessions never
    // cross event years. Fall back to 2026 if year isn't set on the session.
    const scopedYear = props.session?.year || 2026;

    const params = ref({
        per_page: 6,
        page: 1,
        exclude: props.session.id,
        year: scopedYear,
    });
    if (scopedYear === 2026) params.value.unscheduled = true;
    const sessionTags = [];

    if (props.session?.related_sessions?.length) {
        params.value.id = props.session.related_sessions?.join(',');
    }

    if (props.session) {
        Object.keys(props.session).forEach((key) => {
            if (key.startsWith('session_') && Array.isArray(props.session[key]) && props.session[key].length) {
                sessionTags.push(...props.session[key]);
            }
        });
    }

    if (sessionTags.length) {
        params.value.filter_tags = sessionTags.map((tag) => tag.id).join(',');
    }

    // SSR-fetched list, so per-viewer reasons are overlaid in the browser.
    const { load: loadReasons, reasonFor } = useScheduleReasons();
    // See catalog.vue: the feed is shared and cached, so per-viewer state has to
    // be overlaid here or every card renders plain (Nick, Asana 1217708959827796
    // and the surfaces 1217708959827831 generalises).
    const { applyViewerState, needsSeat } = useViewerSessionState();
    onMounted(loadReasons);

    const { data } = await useAsyncData(`related-${scopedYear}-${props.session.id}-${props.variant}`,
        () => $fetch('/api/v1/sessions', { params: { ...params.value } }));

    // Computed rather than applied in place: the overlay reads the user session
    // and the conflict cache, both of which change after this rail has rendered
    // (SSR has no viewer at all), so it has to re-run when they do.
    const viewerSessions = computed(() => applyViewerState(data.value?.sessions ?? []));

    const trackRef = ref(null);
    const canPrev = ref(false);
    const canNext = ref(false);

    const updateScrollState = () => {
        const track = trackRef.value;
        if (!track) return;
        // Use card geometry rather than raw scroll position because the
        // trailing spacer (used so the last card can snap to the leftmost
        // slot) inflates scrollWidth past the actual content end.
        const cards = track.querySelectorAll(':scope > li:not([aria-hidden="true"])');
        const first = cards[0];
        const last = cards[cards.length - 1];
        if (!first || !last) {
            canPrev.value = false;
            canNext.value = false;
            return;
        }
        const trackRect = track.getBoundingClientRect();
        canPrev.value = first.getBoundingClientRect().left < trackRect.left - 2;
        canNext.value = last.getBoundingClientRect().right > trackRect.right + 2;
    };

    const scrollStep = (direction) => {
        const track = trackRef.value;
        if (!track) return;
        const card = track.querySelector(':scope > li');
        if (!card) return;
        const gap = parseFloat(getComputedStyle(track).gap) || 20;
        const amount = (card.offsetWidth + gap) * (direction === 'next' ? 1 : -1);
        track.scrollBy({ left: amount, behavior: 'smooth' });
    };

    const prev = () => scrollStep('prev');
    const next = () => scrollStep('next');

    onMounted(() => {
        const track = trackRef.value;
        if (!track) return;
        track.addEventListener('scroll', updateScrollState, { passive: true });
        nextTick(updateScrollState);
    });

    const isPage = computed(() => props.variant === 'page');
    const allSessionsUrl = '/agenda';

    const cardTheme = computed(() => (isPage.value ? 'dark' : 'light'));
    const pillVariant = computed(() => (isPage.value ? 'pink' : 'magenta'));
    const controlsVariant = computed(() => (isPage.value ? 'pink' : 'magenta'));
    const cardWidth = computed(() => (isPage.value ? 'w-[85vw] md:w-[480px]' : 'w-[85vw] md:w-[440px]'));
</script>

<template>
    <section v-if="data?.sessions?.length"
        class="block-related-sessions relative"
        :class="isPage
            ? 'overflow-hidden py-[60px] md:py-[90px] pl-[var(--box-padding-lr-m)] md:pl-[var(--box-padding-lr)] text-white'
            : 'text-[var(--magenta)] -mr-[var(--box-padding-lr-m)] md:-mr-[40px]'"
        :style="isPage ? 'background: linear-gradient(62.47deg, #550749 4%, var(--purple) 76%);' : ''">

        <!-- Page-variant shard decoration -->
        <img v-if="isPage"
            src="/img/2026/related/page-shard.png"
            srcset="/img/2026/related/page-shard.png 1x, /img/2026/related/page-shard@2x.png 2x"
            width="400" height="598"
            loading="lazy" decoding="async"
            alt="" aria-hidden="true"
            class="hidden lg:display-block absolute top-0 right-0 w-[560px] h-auto pointer-events-none z-0" />

        <div class="relative z-[1] flex flex-col gap-[40px] md:gap-[50px]">
            <!-- Page header: inline headline + pill button on desktop, headline-only on mobile -->
            <div v-if="isPage" class="flex flex-wrap items-center gap-[24px] md:gap-[34px]">
                <h2 class="font-hubspot-serif font-normal m-0 text-[36px] lg:text-[60px] leading-[1.1] whitespace-nowrap">
                    Related Sessions
                </h2>
                <div class="hidden md:block">
                    <FragmentsPillButton
                        :url="allSessionsUrl"
                        label="All Sessions"
                        :variant="pillVariant" />
                </div>
            </div>

            <!-- Drawer header: top border + justify-between on desktop, headline-only on mobile -->
            <div v-else class="pr-[var(--box-padding-lr-m)] md:pr-[40px]">
                <div class="border-t border-[var(--dark-pink)] pt-[40px] md:pt-[60px] flex flex-wrap items-center justify-between gap-[16px]">
                    <h2 class="font-hubspot-serif font-normal m-0 text-[30px] lg:text-[40px] leading-[1.2] whitespace-nowrap">
                        Related Sessions
                    </h2>
                    <div class="hidden md:block">
                        <FragmentsPillButton
                            :url="allSessionsUrl"
                            label="All Sessions"
                            :variant="pillVariant" />
                    </div>
                </div>
            </div>

            <!-- Carousel -->
            <ul ref="trackRef"
                class="related-sessions-grid flex flex-row gap-[20px] overflow-x-auto snap-x snap-mandatory scroll-smooth py-[20px] -my-[20px] pl-[16px] -ml-[16px] scroll-pl-[16px]"
                :class="isPage
                    ? 'pr-[var(--box-padding-lr-m)] md:pr-[var(--box-padding-lr)]'
                    : 'pr-[var(--box-padding-lr-m)] md:pr-[40px]'"
                tabindex="0">
                <li v-for="session in viewerSessions" :key="session.id"
                    class="snap-start shrink-0"
                    :class="cardWidth">
                    <!-- See catalog.vue: the viewer's stored AI reason, so a
                         builder-added session reads the same here as it does on
                         the agenda. This rail is SSR-fetched too, so the reason
                         comes from the client-side overlay. Empty for everyone
                         else. -->
                    <AgendaDetailCard
                        :session="session"
                        :year="session.year || 2026"
                        :recommendation="session.recommendation || reasonFor(session.unique_id)"
                        :is-full="!!session.is_full"
                        :action-required="needsSeat(session)"
                        :on-dark="isPage" />
                </li>
                <!-- Trailing spacer so the last card can snap-start all the
                     way to the left edge of the carousel viewport, instead
                     of getting stuck mid-scroll with earlier cards cropped
                     awkwardly behind it. -->
                <li aria-hidden="true"
                    class="shrink-0 w-[calc(100vw-85vw-24px-24px)]"
                    :class="isPage
                        ? 'md:w-[calc(100vw-(480px+var(--box-padding-lr)+20px))]'
                        : 'md:w-[calc(min(100vw,1120px)-(440px+40px+20px))]'" />
            </ul>

            <!-- Controls + mobile pill button -->
            <div class="flex items-center gap-[16px]"
                :class="isPage ? '' : 'pr-[var(--box-padding-lr-m)] md:pr-[40px]'">
                <FragmentsCarouselControls
                    v-if="data.sessions.length > 1"
                    :has-prev="canPrev"
                    :has-next="canNext"
                    :variant="controlsVariant"
                    prev-label="Previous sessions"
                    next-label="Next sessions"
                    @prev="prev"
                    @next="next" />
                <div class="md:hidden">
                    <FragmentsPillButton
                        :url="allSessionsUrl"
                        label="All Sessions"
                        :variant="pillVariant" />
                </div>
            </div>
        </div>
    </section>
</template>

<style>
.related-sessions-grid {
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.related-sessions-grid::-webkit-scrollbar {
    display: none;
}
</style>
